import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast, Toaster } from "react-hot-toast";

import { methods } from "../../../utils/axios";
import { ButtonLoading } from "../../../components/buttons/button-loading";
import InputPass from "../../../components/inputs/input-pass";
import SliderLogin from "../../../components/sliders/slider-login";
import logo from "../../../assets/gm-logo-name.svg";
import { PASSWORD_RULES } from "../../../helpers/password-rules";
import { ROUTES } from "../../../helpers/constants";

const errorMessage = (error, fallback) => error?.response?.data?.message || fallback;

// Activar una cuenta invitada: el link del correo trae ?token=. La persona pide
// un código a su correo y crea su contraseña. No requiere sesión.
const AcceptInvitation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  // loading | ready | invalid | done
  const [stage, setStage] = useState("loading");
  const [invalidMessage, setInvalidMessage] = useState("");
  const [invitation, setInvitation] = useState({ email: "", name: "" });
  const [codeSent, setCodeSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ code: "", newPassword: "", confirmPassword: "" });

  useEffect(() => {
    if (!token) {
      setInvalidMessage("El link de invitación está incompleto. Ábrelo de nuevo desde el correo que recibiste.");
      setStage("invalid");
      return;
    }
    methods
      .post("/invitations/validate", { token })
      .then(({ data }) => {
        setInvitation(data);
        setStage("ready");
      })
      .catch((error) => {
        setInvalidMessage(errorMessage(error, "No pudimos validar la invitación. Inténtalo de nuevo en unos minutos."));
        setStage("invalid");
      });
  }, [token]);

  const rulesOk = PASSWORD_RULES.every((rule) => rule.test(form.newPassword));
  const matches = form.newPassword !== "" && form.newPassword === form.confirmPassword;
  const canSubmit = /^\d{6}$/.test(form.code) && rulesOk && matches && !saving;

  const onChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const requestCode = async () => {
    setSending(true);
    try {
      await methods.post("/invitations/request-code", { token });
      setCodeSent(true);
      toast.success(`Te enviamos un código a ${invitation.email}`, { duration: 5000 });
    } catch (error) {
      toast.error(errorMessage(error, "No pudimos enviar el código. Inténtalo de nuevo."));
    } finally {
      setSending(false);
    }
  };

  const accept = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      await methods.post("/invitations/accept", { token, code: form.code, newPassword: form.newPassword });
      setStage("done");
    } catch (error) {
      toast.error(errorMessage(error, "No pudimos activar tu cuenta. Inténtalo de nuevo."));
    } finally {
      setSaving(false);
    }
  };

  const goToLogin = () => navigate(ROUTES.LOGIN.route);

  const renderContent = () => {
    if (stage === "loading") {
      return (
        <div className="gm-login-welcome-container">
          <img src={logo} alt="growme-logo" />
          <h1>Cargando invitación...</h1>
        </div>
      );
    }

    if (stage === "invalid") {
      return (
        <>
          <div className="gm-login-welcome-container">
            <img src={logo} alt="growme-logo" />
            <h1>No pudimos abrir la invitación</h1>
            <span>{invalidMessage}</span>
          </div>
          <button type="button" className="gm-force-password-link" onClick={goToLogin}>
            Ir a iniciar sesión
          </button>
        </>
      );
    }

    if (stage === "done") {
      return (
        <>
          <div className="gm-login-welcome-container">
            <img src={logo} alt="growme-logo" />
            <h1>¡Tu cuenta está activa!</h1>
            <span>
              Ya puedes ingresar a GrowMe con <strong>{invitation.email}</strong> y la contraseña que acabas de crear.
            </span>
          </div>
          <div className="gm-login-buttons">
            <ButtonLoading className="gm-button-login-section" type="button" buttonLabel="Iniciar sesión" onClick={goToLogin} />
          </div>
        </>
      );
    }

    return (
      <>
        <div className="gm-login-welcome-container">
          <img src={logo} alt="growme-logo" />
          <h1>Activa tu cuenta</h1>
          <span>
            {invitation.name ? `Hola ${invitation.name}, te` : "Te"} invitaron a GrowMe. Para crear tu contraseña te
            enviaremos un código de verificación a <strong>{invitation.email}</strong>.
          </span>
        </div>

        {!codeSent ? (
          <div className="gm-login-buttons">
            <ButtonLoading
              className="gm-button-login-section"
              type="button"
              buttonLabel="Enviar código"
              onClick={requestCode}
              disabled={sending}
              loading={sending}
            />
          </div>
        ) : (
          <form className="gm-login-form-container" onSubmit={accept}>
            <div className="gm-login-form-group">
              <label htmlFor="code">
                <input
                  className="gm-input-form-section"
                  id="code"
                  name="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="Código de 6 dígitos"
                  value={form.code}
                  onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value.replace(/\D/g, "") }))}
                  disabled={saving}
                  required
                />
              </label>
            </div>
            <InputPass
              className="gm-input-form-section"
              placeholder="Crea tu contraseña"
              id="newPassword"
              name="newPassword"
              autoComplete="new-password"
              value={form.newPassword}
              onChange={onChange}
              disabled={saving}
              required
            />
            <InputPass
              className="gm-input-form-section"
              placeholder="Repite tu contraseña"
              id="confirmPassword"
              name="confirmPassword"
              autoComplete="new-password"
              value={form.confirmPassword}
              onChange={onChange}
              disabled={saving}
              required
            />
            <ul className="gm-force-password-rules">
              {PASSWORD_RULES.map((rule) => (
                <li key={rule.label} className={rule.test(form.newPassword) ? "ok" : ""}>
                  {rule.label}
                </li>
              ))}
              <li className={matches ? "ok" : ""}>Ambas contraseñas coinciden</li>
            </ul>
            <div className="gm-login-buttons">
              <ButtonLoading
                className="gm-button-login-section"
                type="submit"
                buttonLabel="Activar mi cuenta"
                disabled={!canSubmit}
                loading={saving}
              />
            </div>
            <button type="button" className="gm-force-password-link" onClick={requestCode} disabled={sending}>
              ¿No te llegó? Enviar otro código
            </button>
          </form>
        )}
      </>
    );
  };

  return (
    <div className="gm-container-login">
      <div className="gm-form-container">
        <div className="gm-form-section gm-force-password-section">
          <Toaster />
          {renderContent()}
        </div>
      </div>
      <div className="gm-slider-container">
        <SliderLogin />
      </div>
    </div>
  );
};

export default AcceptInvitation;
